<template>
  <div>
    <button
        v-if="!drawer"
        class="team-panel-activator"
        type="button"
        aria-label="TeamPanel öffnen"
        @click="drawer = true"
    >
      <v-icon class="activator-arrow" icon="mdi-chevron-right" size="18" />
    </button>

    <v-navigation-drawer
        v-model="drawer"
        temporary
        class="team-panel-drawer"
        width="340"
    >
      <div class="drawer-hero">
        <div class="drawer-hero-icon">
          <v-icon icon="mdi-shield-crown-outline" size="34" />
        </div>
        <div>
          <p>Administration</p>
          <h2>TeamPanel</h2>
        </div>
      </div>

      <v-list class="team-nav-list" density="compact" nav>
        <div v-for="section in visibleSections" :key="section.title">
          <div class="section-label">{{ section.title }}</div>
          <v-list-item
              v-for="item in section.items"
              :key="item.value"
              :to="item.to"
              :prepend-icon="item.icon"
              :title="item.title"
              :value="item.value"
              :active="isActive(item)"
              @click="drawer = false"
          >
            <template v-if="item.accent" v-slot:append>
              <v-chip color="primary" size="x-small" variant="tonal">{{ item.accent }}</v-chip>
            </template>
          </v-list-item>
        </div>
      </v-list>
    </v-navigation-drawer>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import { useQuery } from '@vue/apollo-composable';
import { useRoute } from 'vue-router';
import gql from 'graphql-tag';
import { Utils } from '@/modules/utils';

const drawer = ref(false);
const route = useRoute();

const requestPermissions = [
  'acp.players.view',
  'acp.character.rp.de.list',
  'acp.gang.rp.de.list',
  'acp.property.rp.de.list',
  'acp.downloads.edit',
  'acp.messages.rp.de.view',
  'acp.jobs.rp.de.view',
  'acp.tools.rp.de.charrename',
  'acp.tools.rp.de.chardelete',
  'acp.tools.rp.de.transfermoney',
  'acp.de.chats',
  'network.permissions.edit',
  'acp.player.migrate',
  'acp.logs.view',
  'acp.support.statistics.view',
];

const { result } = useQuery(gql`
query permissions ($permissions: [String]) {
  permissions(permissions: $permissions)
}
`, { permissions: Utils.getMultiLanguagePermissions(requestPermissions) });

const permissions = computed(() => result.value?.permissions ?? []);

const hasOneLanguagePermission = (permission) => {
  const inPermissions = Utils.getMultiLanguagePermissions([permission]);
  return inPermissions.some((p) => permissions.value.includes(p));
};

const hasPermission = (item) => {
  if (item.permission) return permissions.value.includes(item.permission);
  if (item.languagePermission) return hasOneLanguagePermission(item.languagePermission);
  return true;
};

const sections = computed(() => [
  {
    title: 'Verwaltung',
    items: [
      {
        permission: 'acp.players.view',
        to: '/admin/players',
        icon: 'mdi-account-group-outline',
        title: 'Spieler',
        value: 'players',
        activePath: '/admin/players',
      },
      {
        languagePermission: 'acp.character.rp.XX.list',
        to: '/admin/characters',
        icon: 'mdi-account-box-multiple-outline',
        title: 'Charakter',
        value: 'characters',
        activePath: '/admin/characters',
      },
      {
        languagePermission: 'acp.XX.chats',
        to: '/admin/chats',
        icon: 'mdi-forum-outline',
        title: 'Anfragen',
        value: 'chats',
        activePath: '/admin/chats',
        // accent: 'Live',
      },
    ],
  },
  {
    title: 'Tools',
    items: [
      {
        languagePermission: 'acp.tools.rp.XX.charrename',
        to: '/admin/tools/charrename',
        icon: 'mdi-account-edit-outline',
        title: 'RenameChar',
        value: 'renamechar',
        activePath: '/admin/tools/charrename',
      },
      {
        languagePermission: 'acp.tools.rp.XX.chardelete',
        to: '/admin/tools/chardelete',
        icon: 'mdi-delete-outline',
        title: 'DeleteChar',
        value: 'deletechar',
        activePath: '/admin/tools/chardelete',
      },
      {
        languagePermission: 'acp.tools.rp.XX.transfermoney',
        to: '/admin/tools/transfermoney',
        icon: 'mdi-cash-multiple',
        title: 'TransferMoney',
        value: 'transfermoney',
        activePath: '/admin/tools/transfermoney',
      },
      {
        permission: 'acp.player.migrate',
        to: '/admin/tools/migrate',
        icon: 'mdi-arrow-left-right',
        title: 'Migrate',
        value: 'migrate',
        activePath: '/admin/tools/migrate',
      },
    ],
  },
  {
    title: 'Daten',
    items: [
      {
        languagePermission: 'acp.messages.rp.XX.view',
        to: '/admin/messages',
        icon: 'mdi-message-text-outline',
        title: 'Messages',
        value: 'messages',
        activePath: '/admin/messages',
      },
      {
        languagePermission: 'acp.jobs.rp.XX.view',
        to: '/admin/characterjobs',
        icon: 'mdi-arm-flex-outline',
        title: 'JobQuery',
        value: 'jobquery',
        activePath: '/admin/characterjobs',
      },
      {
        languagePermission: 'acp.gang.rp.XX.list',
        to: '/admin/gangs',
        icon: 'mdi-skull-scan-outline',
        title: 'Gangs',
        value: 'gangs',
        activePath: '/admin/gangs',
      },
      {
        languagePermission: 'acp.property.rp.XX.list',
        to: '/admin/properties',
        icon: 'mdi-home-city-outline',
        title: 'Immobilien',
        value: 'properties',
        activePath: '/admin/properties',
      },
      {
        permission: 'acp.downloads.edit',
        to: '/admin/downloads',
        icon: 'mdi-download-outline',
        title: 'Downloads',
        value: 'downloads',
        activePath: '/admin/downloads',
      },
      {
        permission: 'acp.support.statistics.view',
        to: '/admin/moddashboard',
        icon: 'mdi-monitor-account',
        title: 'ModDashboard',
        value: 'moddashboard',
        activePath: '/admin/moddashboard',
      },
    ],
  },
  {
    title: 'System',
    items: [
      {
        permission: 'network.permissions.edit',
        to: '/admin/permissions',
        icon: 'mdi-security',
        title: 'Permissions',
        value: 'permissions',
        activePath: '/admin/permissions',
      },
      {
        permission: 'acp.logs.view',
        to: '/admin/acplogs',
        icon: 'mdi-text-box-search-outline',
        title: 'ACP Logs',
        value: 'acpLogs',
        activePath: '/admin/acplogs',
      },
    ],
  },
]);

const visibleSections = computed(() => sections.value
  .map((section) => ({
    ...section,
    items: section.items.filter(hasPermission),
  }))
  .filter((section) => section.items.length > 0));

const isActive = (item) => route.path.toLowerCase().startsWith(item.activePath.toLowerCase());
</script>

<style scoped>
.team-panel-activator {
  position: fixed;
  z-index: 100;
  top: 42%;
  left: 0;
  display: grid;
  place-items: center;
  align-items: center;
  width: 34px;
  height: 58px;
  padding: 0;
  border: 1px solid rgba(124, 232, 234, 0.22);
  border-left: 0;
  border-radius: 0 8px 8px 0;
  background:
    linear-gradient(135deg, rgba(28, 176, 179, 0.2), rgba(255, 193, 7, 0.08)),
    rgb(var(--v-theme-site-chrome));
  color: white;
  box-shadow: 0 16px 38px rgba(0, 0, 0, 0.32);
  cursor: pointer;
  transition: border-color 0.2s ease, background 0.2s ease;
}

.team-panel-activator:hover,
.team-panel-activator:focus-visible {
  border-color: rgba(124, 232, 234, 0.58);
  background:
    linear-gradient(135deg, rgba(28, 176, 179, 0.28), rgba(255, 193, 7, 0.1)),
    rgb(var(--v-theme-site-chrome));
}

.activator-arrow {
  color: rgb(var(--v-theme-primary-light));
}

.team-panel-drawer {
  border-right: 1px solid rgba(124, 232, 234, 0.16);
  background:
    linear-gradient(135deg, rgba(28, 176, 179, 0.12), transparent 42%),
    rgb(var(--v-theme-site-chrome)) !important;
}

.drawer-hero {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: 10px;
  align-items: center;
  margin: 10px 10px 6px;
  padding: 10px 12px;
  border: 1px solid rgba(124, 232, 234, 0.16);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.045);
}

.drawer-hero-icon {
  display: grid;
  place-items: center;
  width: 40px;
  height: 40px;
  border-radius: 7px;
  background: rgba(28, 176, 179, 0.16);
  color: rgb(var(--v-theme-primary-light));
}

.drawer-hero p {
  margin: 0 0 2px;
  color: rgb(var(--v-theme-primary-light));
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  line-height: 1.1;
}

.drawer-hero h2 {
  margin: 0;
  color: white !important;
  font-size: 18px;
  line-height: 1;
}

.team-nav-list {
  padding: 4px 8px 18px;
}

.section-label {
  margin: 18px 14px 7px;
  color: rgba(255, 255, 255, 0.48);
  font-size: 12px;
  font-weight: 900;
  text-transform: uppercase;
}

.team-nav-list :deep(.v-list-item) {
  margin-inline: 6px;
  border-radius: 8px;
}

.team-nav-list :deep(.v-list-item--active) {
  background: rgba(28, 176, 179, 0.15);
  color: white;
}

@media screen and (max-width: 780px) {
  .team-panel-activator {
    top: auto;
    bottom: 18px;
    left: 0;
    width: 34px;
    height: 54px;
  }
}
</style>
